import React, { MutableRefObject, useCallback, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { SuggestionInterface } from '@components/ui/AutoComplete';
import { useCitiesSuggestions } from '@services/cities';
import { FuelIcon, FuelSearchbox } from '@frete.com/fuel-react';
import { StateMap } from '@utils/StateMap';
import useDebounce from '@hooks/useDebounce';
import AutoCompleteList from '@components/ui/AutoComplete/List';
import { FieldCityPropsInterface, OnFuelInputEvent } from './FieldCityTypes';
import {
  DEBOUNCE_DELAY,
  LABELS,
  MESSAGES,
  MINIMUM_SEARCH_LENGTH,
} from './constants';

function FieldCity({ flow }: FieldCityPropsInterface) {
  const {
    control,
    clearErrors,
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext();

  const [valueCityState, setValueCityState] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState<SuggestionInterface | null>(
    null,
  );

  const searchboxRef = useRef() as MutableRefObject<HTMLFuelSearchboxElement>;

  const fieldNames = {
    city: `waypoints.${flow}.place.city.name`,
    stateName: `waypoints.${flow}.place.state.name`,
    stateAcronym: `waypoints.${flow}.place.state.acronym`,
  };

  const label = flow === 'delivery' ? LABELS.delivery : LABELS.origin;
  const hasError = !!errors.waypoints?.[flow]?.place.city?.name;

  const { data: suggestions } = useCitiesSuggestions(searchQuery);
  const { debounce } = useDebounce(DEBOUNCE_DELAY);

  const setCity = useCallback(
    (cityParam: SuggestionInterface) => {
      const { name: city, stateCode, description } = cityParam;

      setValue(fieldNames.city, city);
      setValue(fieldNames.stateAcronym, stateCode || '');
      setValue(fieldNames.stateName, stateCode ? StateMap[stateCode] : '');
      setValueCityState(description);
      setSelectedCity(cityParam);
    },
    [setValue, fieldNames],
  );

  const clearErrorCity = useCallback(() => {
    clearErrors(fieldNames.city);
    clearErrors(fieldNames.stateName);
    clearErrors(fieldNames.stateAcronym);
  }, [clearErrors, fieldNames]);

  const resetFields = useCallback(() => {
    setValueCityState('');
    setSelectedCity(null);
    setValue(fieldNames.city, '');
    setValue(fieldNames.stateName, '');
    setValue(fieldNames.stateAcronym, '');
  }, [setValue, fieldNames]);

  const handleOnFuelInput = useCallback(
    (event: CustomEvent<OnFuelInputEvent>) => {
      if (event.detail?.value.length > MINIMUM_SEARCH_LENGTH) {
        searchboxRef.current.openDropdown();
      }
      const value = event.detail?.value || '';
      setValueCityState(value);

      if (selectedCity && value !== selectedCity.description) {
        setSelectedCity(null);
      }

      debounce(() => {
        setSearchQuery(value);
      });
    },
    [selectedCity, debounce],
  );

  const handleOnFuelClick = useCallback(() => {
    if (valueCityState.length < MINIMUM_SEARCH_LENGTH) {
      searchboxRef.current.closeDropdown();
    }
  }, [valueCityState]);

  const handleClearSelectedSuggestion = useCallback(() => {
    setValueCityState('');
    setSearchQuery('');
    setSelectedCity(null);
  }, []);

  const handleSetCity = useCallback(
    async (cityParam: SuggestionInterface) => {
      clearErrorCity();
      setCity(cityParam);
      await searchboxRef.current.closeDropdown();
    },
    [clearErrorCity, setCity],
  );

  const handleOnBlur = useCallback(async () => {
    const isValidSelection =
      selectedCity && valueCityState === selectedCity.description;

    if (!isValidSelection && valueCityState.length > 0) {
      resetFields();
    }

    await trigger(fieldNames.city);
  }, [selectedCity, valueCityState, resetFields, trigger, fieldNames.city]);

  return (
    <Controller
      name={fieldNames.city}
      control={control}
      render={({ field: { onBlur } }) => {
        return (
          <FuelSearchbox
            key={hasError ? 'error' : 'default'}
            label={label}
            hint={MESSAGES.hint}
            state={hasError ? 'error' : 'default'}
            ref={searchboxRef}
            name={fieldNames.city}
            value={valueCityState}
            onBlur={async () => {
              onBlur();
              await handleOnBlur();
            }}
            placeholder={MESSAGES.placeholder}
            onFuel-input={handleOnFuelInput}
            onFuel-clear={handleClearSelectedSuggestion}
            onClick={handleOnFuelClick}
          >
            <div slot="items">
              <AutoCompleteList
                name={`field-item-${fieldNames.city}`}
                emptyMessage={MESSAGES.emptyResults}
                leftIcon={<FuelIcon size="small" name="pin" />}
                searchTerm={valueCityState}
                suggestionsList={suggestions || []}
                onSelectSuggestion={handleSetCity}
              />
            </div>
          </FuelSearchbox>
        );
      }}
      defaultValue={valueCityState}
    />
  );
}

export default FieldCity;
